import { useState, useEffect } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Pencil, Trash2, RotateCcw } from "lucide-react"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { FilterBar } from "@/app/components/FilterBar/FilterBar"
import { SearchInput } from "@/app/components/FilterBar/SearchInput"
import { MultiSelectFilter } from "@/app/components/FilterBar/MultiSelectFilter"
import { SelectFilter } from "@/app/components/FilterBar/SelectFilter"
import { SegmentedControl } from "@/app/components/FilterBar/SegmentedControl"
import { SortIcon } from "@/app/components/SortIcon"
import { TableColumnHeader } from "@/app/components/TableColumnHeader"
import { TableEmptyState, EmptyStateContent } from "@/app/components/TableEmptyState"
import { DataCard } from "@/app/components/DataCard"
import { MobileSortSelect } from "@/app/components/MobileSortSelect"
import { PaginationControls } from "@/app/components/PaginationControls"
import { formatNumber, decimalToPercent } from "@/app/lib/format"
import { stickyTh } from "@/app/lib/tableStyles"
import { ACQ_GREEN, DIV_RED, txTypeColor } from "@/app/lib/txColors"
import { interestTypeLabel } from "@/app/lib/interestType"
import { ErrorBanner, formatApiError } from "@/app/components/FormPrimitives"
import type { ClientTheme } from "@/app/lib/themes"
import type { InterestKind } from "@/app/pages/NewTransactionPage/types"
import type { SortDir } from "@/app/hooks/useTableSort"
import type {
  TransactionsMeta,
  TransactionsSummary,
  FilterOptions,
  PerPage,
} from "./useTransactionsView"
import { TransactionRow, type Transaction } from "./TransactionRow"
import { TransactionEditModal } from "./TransactionEditModal"
import { EditedBadge } from "./EditedBadge"

interface Props {
  // Current page of rows + server-computed pagination/summary.
  rows: Transaction[]
  meta: TransactionsMeta
  summary: TransactionsSummary
  canWrite: boolean
  showDeleted: boolean
  onToggleDeleted: (v: boolean) => void

  // Tab.
  kind: InterestKind
  onKindChange: (k: InterestKind) => void

  // Filters.
  q: string
  onQChange: (v: string) => void
  sectionSel: string[]
  onSectionChange: (v: string[]) => void
  trSel: string[]
  onTrChange: (v: string[]) => void
  typeSel: string[]
  onTypeChange: (v: string[]) => void
  interestSel: string[]
  onInterestChange: (v: string[]) => void
  hasOrriSel: string
  onHasOrriChange: (v: string) => void
  hasFilters: boolean
  onClearFilters: () => void
  options: FilterOptions

  // Sort (server-driven; semantics mirror useTableSort).
  sortKey: string | null
  sortDir: SortDir
  onSort: (key: string) => void

  // Pagination.
  page: number
  perPage: PerPage
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: PerPage) => void
  isFetching: boolean

  // The page-header Export CSV button lives in TransactionsPage; the table
  // owns the current view (tab + filters + sort), so it hands the export fn
  // up via ref (mirrors InventoryTable).
  onExport: () => void | Promise<void>
  exportRef?: React.MutableRefObject<(() => void) | null>
}

export function TransactionsTable({
  rows,
  meta,
  summary,
  canWrite,
  showDeleted,
  onToggleDeleted,
  kind,
  onKindChange,
  q,
  onQChange,
  sectionSel,
  onSectionChange,
  trSel,
  onTrChange,
  typeSel,
  onTypeChange,
  interestSel,
  onInterestChange,
  hasOrriSel,
  onHasOrriChange,
  hasFilters,
  onClearFilters,
  options,
  sortKey,
  sortDir,
  onSort,
  page,
  perPage,
  onPageChange,
  onPerPageChange,
  isFetching,
  onExport,
  exportRef,
}: Props) {
  const { theme } = useClientTheme()
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()

  const [hoveredRow, setHoveredRow] = useState<number | null>(null)
  const [editing, setEditing] = useState<Transaction | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  // Invalidate every read that recomputes from transaction history. The singular
  // "inventory" key matters — the STR drill-down modal keys on ["inventory", str].
  const invalidate = () => {
    for (const key of ["transactions", "inventories", "orri_inventories", "inventory"]) {
      queryClient.invalidateQueries({ queryKey: [key] })
    }
  }
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch(`/transactions/${editing!.id}`, payload),
    onSuccess: () => {
      invalidate()
      setEditing(null)
    },
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.del(`/transactions/${id}`),
    onSuccess: () => {
      invalidate()
      setConfirmDelete(null)
    },
  })
  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.post(`/transactions/${id}/restore`),
    onSuccess: () => invalidate(),
  })

  const colSpan = canWrite ? 12 : 11

  // Hand the export fn (bound to the current tab + filters + sort, owned by
  // the view hook) up to the page, where the header button lives.
  useEffect(() => {
    if (!exportRef) return
    exportRef.current = () => {
      void onExport()
    }
    return () => {
      exportRef.current = null
    }
  }, [exportRef, onExport])

  const from = meta.total_count === 0 ? 0 : (meta.page - 1) * meta.per_page + 1
  const to = Math.min(meta.page * meta.per_page, meta.total_count)
  const footerCountText =
    meta.total_pages > 1 || hasFilters
      ? `Showing ${from}–${to} of ${meta.total_count} transactions`
      : `${meta.total_count} total transactions`

  const acqAcres = Number(summary.acq_acres)
  const divAcres = Number(summary.div_acres)
  const netAcres = Number(summary.net_acres)

  return (
    // Desktop continues the page's viewport-fit flex chain so the card (and
    // its footer) always fits on screen; mobile stays in document flow — the
    // card list needs the page itself to scroll, and the card's
    // overflow-hidden would clip it if height-constrained.
    <div className={isMobile ? undefined : "flex min-h-0 flex-1 flex-col"}>
      {/* Interest-kind tabs, styled like the New Transaction page's tab bar. */}
      <div className="mb-4 flex flex-none items-center justify-between gap-3">
        <SegmentedControl
          value={kind}
          ariaLabel="Interest kind"
          accents={theme.kindAccents}
          onChange={(next) => onKindChange(next as InterestKind)}
          options={[
            { label: "Leasehold", value: "leasehold" },
            { label: "Mineral", value: "mineral" },
          ]}
          theme={theme}
        />
      </div>

      <div
        className={`overflow-hidden ${isMobile ? "" : "flex min-h-0 flex-1 flex-col"}`}
        style={{
          border: `1px solid ${theme.borderColor}`,
          borderRadius: "8px",
          backgroundColor: theme.bgSurface,
        }}
      >
        <FilterBar
          hasFilters={hasFilters}
          onClear={onClearFilters}
          resultCount={meta.total_count}
          totalCount={options.total_count ?? meta.total_count}
          entityLabel="transaction"
          theme={theme}
        >
          <SearchInput
            value={q}
            onChange={onQChange}
            placeholder="Search STR or TX #…"
            theme={theme}
          />
          <MultiSelectFilter
            selected={sectionSel}
            onChange={onSectionChange}
            placeholder="All Sections"
            options={options.sections}
            width="150px"
            theme={theme}
          />
          <MultiSelectFilter
            selected={trSel}
            onChange={onTrChange}
            placeholder="All T-R"
            options={options.township_ranges}
            theme={theme}
          />
          <MultiSelectFilter
            selected={typeSel}
            onChange={onTypeChange}
            placeholder="All Types"
            options={options.types}
            width="160px"
            theme={theme}
          />
          <MultiSelectFilter
            selected={interestSel}
            onChange={onInterestChange}
            placeholder="All Interest Statuses"
            options={options.interest_types}
            width="180px"
            theme={theme}
          />
          {/* ORRI only exists on leasehold rows — hide the filter on mineral. */}
          {kind === "leasehold" && (
            <SelectFilter
              value={hasOrriSel}
              onChange={onHasOrriChange}
              placeholder="Has ORRI: All"
              options={[
                { label: "Has ORRI", value: "yes" },
                { label: "No ORRI", value: "no" },
              ]}
              width="150px"
              theme={theme}
            />
          )}
          {canWrite && (
            <label
              className="flex items-center gap-2 flex-shrink-0"
              style={{ cursor: "pointer", height: "34px" }}
            >
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => onToggleDeleted(e.target.checked)}
              />
              <span
                style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textMuted }}
              >
                Show deleted
              </span>
            </label>
          )}
        </FilterBar>

        {/* An audited mutating action must not fail invisibly. */}
        {(deleteMutation.isError || restoreMutation.isError) && (
          <div className="px-3.5">
            <ErrorBanner
              theme={theme}
              message={formatApiError(deleteMutation.error ?? restoreMutation.error)}
            />
          </div>
        )}

        {/* Mobile: card reflow. Desktop: full table. */}
        {isMobile ? (
          rows.length === 0 ? (
            <div className="px-3.5 py-10">
              <EmptyStateContent
                message="No transactions match your filters"
                onClear={onClearFilters}
                theme={theme}
              />
            </div>
          ) : (
            <>
              <MobileSortSelect
                options={[
                  { key: "tx_number", label: "TX #" },
                  { key: "transaction_date", label: "Date" },
                  { key: "type", label: "Type" },
                  { key: "interest_type", label: "Interest Status" },
                  { key: "str", label: "STR" },
                  { key: "net_acres", label: "Net Acres" },
                  ...(kind === "leasehold"
                    ? [
                        { key: "nri", label: "NRI" },
                        { key: "orri_reserved", label: "ORRI Res" },
                      ]
                    : [{ key: "royalty", label: "Royalty" }]),
                ]}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <div className="flex flex-col gap-2.5 p-3">
                {rows.map((tx) => {
                  const isAcq = tx.type === "ACQ"
                  const valueColor = txTypeColor(tx.type, Number(tx.net_acres))
                  const orriNum = Number(tx.orri_reserved)
                  const isDeleted = !!tx.deleted_at
                  // Strike only the card content, never the action footer — the
                  // Restore label must stay legible on a deleted row.
                  const strike = (node: React.ReactNode) =>
                    isDeleted ? (
                      <span style={{ textDecoration: "line-through" }}>{node}</span>
                    ) : (
                      node
                    )
                  return (
                    <div key={tx.id} style={{ opacity: isDeleted ? 0.55 : 1 }}>
                      <DataCard
                        title={
                          <span className="inline-flex items-center gap-2">
                            {strike(tx.tx_number)}
                            {tx.app_edited_at && <EditedBadge theme={theme} />}
                          </span>
                        }
                        accessory={
                          <span
                            className="inline-block px-2 py-0.5 rounded-full"
                            style={{
                              fontFamily: "var(--font-ui)",
                              fontSize: "11px",
                              fontWeight: 500,
                              backgroundColor: `${valueColor}1A`,
                              color: valueColor,
                            }}
                          >
                            {isAcq ? "Acquisition" : tx.type === "DIV" ? "Divestiture" : tx.type}
                          </span>
                        }
                        fields={[
                          { label: "Date", value: strike(tx.transaction_date) },
                          { label: "STR", value: strike(tx.str), mono: true },
                          { label: "T-R", value: strike(tx.township_range) },
                          {
                            label: "Interest Status",
                            value: strike(
                              (() => {
                                if (!tx.interest_type) return "—"
                                const full = interestTypeLabel(tx.interest_type)
                                return full === tx.interest_type
                                  ? tx.interest_type
                                  : `${tx.interest_type} — ${full}`
                              })()
                            ),
                          },
                          {
                            label: "Net Acres",
                            value: strike(
                              <span style={{ color: valueColor, fontWeight: 500 }}>
                                {formatNumber(tx.net_acres, 4)}
                              </span>
                            ),
                            mono: true,
                          },
                          ...(kind === "leasehold"
                            ? [
                                {
                                  label: "NRI",
                                  value: strike(formatNumber(tx.nri, 4)),
                                  mono: true,
                                },
                                {
                                  label: "ORRI Res",
                                  value: strike(orriNum > 0 ? decimalToPercent(orriNum, 2) : "—"),
                                  mono: true,
                                },
                              ]
                            : [
                                {
                                  label: "Royalty",
                                  value: strike(
                                    tx.royalty == null ? "—" : formatNumber(tx.royalty, 4)
                                  ),
                                  mono: true,
                                },
                                {
                                  label: "Legal Desc",
                                  full: true,
                                  value: strike(tx.legal_desc ?? "—"),
                                },
                              ]),
                          {
                            label: "Counterparty",
                            full: true,
                            value: strike(tx.counterparty ?? "—"),
                          },
                          { label: "Book / Page", full: true, value: strike(tx.book_page ?? "—") },
                        ]}
                        footer={
                          canWrite ? (
                            isDeleted ? (
                              <button
                                type="button"
                                onClick={() => restoreMutation.mutate(tx.id)}
                                disabled={restoreMutation.isPending}
                                className="btn"
                                style={iconButton(theme)}
                              >
                                <RotateCcw size={13} /> Restore
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    saveMutation.reset()
                                    setEditing(tx)
                                  }}
                                  className="btn"
                                  style={iconButton(theme)}
                                >
                                  <Pencil size={13} /> Edit
                                </button>
                                {confirmDelete === tx.id ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => deleteMutation.mutate(tx.id)}
                                      disabled={deleteMutation.isPending}
                                      className="btn"
                                      style={{ ...iconButton(theme), color: theme.brandAccent }}
                                    >
                                      Confirm
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setConfirmDelete(null)}
                                      className="btn"
                                      style={iconButton(theme)}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setConfirmDelete(tx.id)
                                      deleteMutation.reset()
                                    }}
                                    className="btn"
                                    style={{ ...iconButton(theme), color: theme.brandAccent }}
                                  >
                                    <Trash2 size={13} /> Delete
                                  </button>
                                )}
                              </>
                            )
                          ) : undefined
                        }
                      />
                    </div>
                  )
                })}
              </div>
            </>
          )
        ) : (
          // The scroll region absorbs exactly the leftover card height (the
          // flex chain from <main> down) — the old maxHeight: 70vh guess made
          // header + tabs + filters + 70vh overflow the viewport, pushing the
          // KPI/pagination footer below the fold.
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr
                  style={{
                    backgroundColor: theme.bgSurface2,
                    borderBottom: `1px solid ${theme.borderColor}`,
                  }}
                >
                  <th
                    className="px-2 py-1 text-left"
                    style={{ ...stickyTh(theme), width: "1px" }}
                  />
                  <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                    <button
                      className="flex items-center gap-1"
                      onClick={() => onSort("transaction_date")}
                    >
                      <TableColumnHeader label="Date" theme={theme} />
                      <SortIcon
                        col="transaction_date"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        theme={theme}
                      />
                    </button>
                  </th>
                  <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                    <button className="flex items-center gap-1" onClick={() => onSort("type")}>
                      <TableColumnHeader label="Type" theme={theme} />
                      <SortIcon col="type" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                    </button>
                  </th>
                  <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                    <button className="flex items-center gap-1" onClick={() => onSort("str")}>
                      <TableColumnHeader label="STR" theme={theme} />
                      <SortIcon col="str" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                    </button>
                  </th>
                  <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                    <TableColumnHeader label="T-R" theme={theme} />
                  </th>
                  {/* Shrink-to-content: cells only ever hold PT / HBP pills, so
                  the abbreviated header sets the column width. */}
                  <th
                    className="px-3 py-3 text-left"
                    style={{ ...stickyTh(theme), width: "1%", whiteSpace: "nowrap" }}
                  >
                    <button
                      className="flex items-center gap-1"
                      onClick={() => onSort("interest_type")}
                      title="Interest Status"
                    >
                      <TableColumnHeader label="Int. Status" theme={theme} />
                      <SortIcon
                        col="interest_type"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        theme={theme}
                      />
                    </button>
                  </th>
                  <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                    <button
                      className="flex items-center gap-1 ml-auto"
                      onClick={() => onSort("net_acres")}
                    >
                      <TableColumnHeader label="Net Acres" theme={theme} />
                      <SortIcon col="net_acres" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                    </button>
                  </th>
                  {kind === "leasehold" ? (
                    <>
                      <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                        <button
                          className="flex items-center gap-1 ml-auto"
                          onClick={() => onSort("nri")}
                        >
                          <TableColumnHeader label="NRI" theme={theme} />
                          <SortIcon col="nri" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                        </button>
                      </th>
                      <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                        <button
                          className="flex items-center gap-1 ml-auto"
                          onClick={() => onSort("orri_reserved")}
                        >
                          <TableColumnHeader label="ORRI Res" theme={theme} />
                          <SortIcon
                            col="orri_reserved"
                            sortKey={sortKey}
                            sortDir={sortDir}
                            theme={theme}
                          />
                        </button>
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                        <button
                          className="flex items-center gap-1 ml-auto"
                          onClick={() => onSort("royalty")}
                        >
                          <TableColumnHeader label="Royalty" theme={theme} />
                          <SortIcon
                            col="royalty"
                            sortKey={sortKey}
                            sortDir={sortDir}
                            theme={theme}
                          />
                        </button>
                      </th>
                      <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                        <TableColumnHeader label="Legal Desc" theme={theme} />
                      </th>
                    </>
                  )}
                  <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                    <TableColumnHeader label="Counterparty" theme={theme} />
                  </th>
                  <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                    <TableColumnHeader label="Book / Page" theme={theme} />
                  </th>
                  {canWrite && (
                    <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                      <TableColumnHeader label="Actions" theme={theme} />
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <TableEmptyState
                    message="No transactions match your filters"
                    onClear={onClearFilters}
                    colSpan={colSpan}
                    theme={theme}
                  />
                ) : (
                  rows.map((tx, index) => (
                    <TransactionRow
                      key={tx.id}
                      tx={tx}
                      kind={kind}
                      index={index}
                      hoveredRow={hoveredRow}
                      onMouseEnter={() => setHoveredRow(index)}
                      onMouseLeave={() => setHoveredRow(null)}
                      theme={theme}
                      canWrite={canWrite}
                      onEdit={() => {
                        saveMutation.reset()
                        setEditing(tx)
                      }}
                      onDelete={() => {
                        setConfirmDelete(tx.id)
                        deleteMutation.reset()
                      }}
                      onRestore={() => restoreMutation.mutate(tx.id)}
                      confirmingDelete={confirmDelete === tx.id}
                      onConfirmDelete={() => deleteMutation.mutate(tx.id)}
                      onCancelDelete={() => setConfirmDelete(null)}
                      deleting={deleteMutation.isPending}
                      restoring={restoreMutation.isPending}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div
          className="flex flex-none items-center justify-between gap-3 flex-wrap px-3.5 py-3 border-t"
          style={{ borderTopColor: theme.borderColor, backgroundColor: theme.bgSurface2 }}
        >
          <span style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textMuted }}>
            {footerCountText}
          </span>
          <div className="flex items-center gap-4 flex-wrap">
            <span
              style={{ fontFamily: "var(--font-data)", fontSize: "12px", color: theme.textMuted }}
            >
              Acq:{" "}
              <span style={{ color: ACQ_GREEN, fontWeight: 500 }}>{formatNumber(acqAcres, 4)}</span>
            </span>
            <span
              style={{ fontFamily: "var(--font-data)", fontSize: "12px", color: theme.textMuted }}
            >
              Div:{" "}
              <span style={{ color: DIV_RED, fontWeight: 500 }}>{formatNumber(divAcres, 4)}</span>
            </span>
            <span
              style={{ fontFamily: "var(--font-data)", fontSize: "12px", color: theme.textMuted }}
            >
              Net:{" "}
              <span style={{ color: theme.textPrimary, fontWeight: 500 }}>
                {formatNumber(netAcres, 4)}
              </span>
            </span>
            <PaginationControls
              page={page}
              totalPages={meta.total_pages}
              perPage={perPage}
              onPageChange={onPageChange}
              onPerPageChange={onPerPageChange}
              disabled={isFetching}
              theme={theme}
            />
          </div>
        </div>

        {editing && (
          <TransactionEditModal
            tx={editing}
            onSubmit={(p) => saveMutation.mutate(p)}
            onClose={() => setEditing(null)}
            saving={saveMutation.isPending}
            error={saveMutation.isError ? formatApiError(saveMutation.error) : null}
            theme={theme}
          />
        )}
      </div>
    </div>
  )
}

// Inline action button used in the mobile card footers (mirrors UnitsPage).
function iconButton(theme: ClientTheme): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontFamily: "var(--font-ui)",
    fontSize: "12px",
    fontWeight: 500,
    padding: "5px 9px",
    borderRadius: "7px",
    backgroundColor: theme.bgSurface2,
    color: theme.textMuted,
    border: `1px solid ${theme.borderColor}`,
    cursor: "pointer",
  }
}
